import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { TOOLTIP_OPENED_EVENT_NAME } from 'jl-design-system/components/tooltip/Tooltip';
import validate from 'jl-design-system/form/validation/validate';
import env from 'jl-design-system/utils/env/env';
import { MARKETING_PREFERENCES_TOOLTIP_ID } from '../../../utils/form/configs/marketingPreferences';
import {
  AUTHENTICATE_PAYPAL_PAYMENT,
  CREATE_PAYPAL_PAYMENT,
  GET_PAYPAL_CLIENT_TOKEN,
  SET_PAYPAL_SERVICE_UNAVAILABLE,
  PAYPAL_PAYMENT_CANCEL,
  PAYPAL_PAYMENT_FAILED,
  UPDATE_BILLING_ADDRESS,
  PAYPAL_STUBS_MODAL_CLOSE,
  PAYPAL_STUBS_MODAL_OPEN,
  PAYPAL_PAYMENT_STARTED,
  PAYPAL_PAYMENT_ENDED,
  PAYPAL_SCRIPTS_LOADED,
  PAYPAL_SCRIPTS_LOADING,
  PAYPAL_SCRIPTS_FAILED,
} from '../../../constants/actionConstants';
import {
  URL_AUTHENTICATE_PAYPAL_PAYMENT,
  URL_GET_PAYPAL_CLIENT_TOKEN,
} from '../../../constants/endpointConstants';
import errorCodeConstants from '../../../constants/errorCodeConstants';
import { updateBillingAddress } from './cardPaymentActions';
import { initPaymentPage } from './orderFormActions';
import { submitOrderAndRedirect } from './submitOrderActions';
import { getBillingAddress } from '../../../utils/address/addressHelpers';
import getPayPalBillingDetailsFormConfig from '../../../utils/form/configs/payPalBillingAddress';
import { loadScript } from '../../../utils';
import isPayPalAvailable from '../../../utils/payment/isPayPalAvailable';

export const getPayPalClientToken = (showError = true) => ({
  type: GET_PAYPAL_CLIENT_TOKEN,
  request: client => client({ path: URL_GET_PAYPAL_CLIENT_TOKEN, config: { method: 'GET' } }),
  showError,
});

const authenticatePayPalPayment = body => ({
  type: AUTHENTICATE_PAYPAL_PAYMENT,
  request: client => client({ path: URL_AUTHENTICATE_PAYPAL_PAYMENT, config: { method: 'POST', body } }),
});

export const getPayPalPaymentRequest = () => async (dispatch, getState) => {
  const state = getState();
  const payPalPaymentProcessing = get(state, 'payment.payPalPaymentProcessing');

  if (payPalPaymentProcessing) dispatch({ type: PAYPAL_PAYMENT_ENDED });
  dispatch({ type: CREATE_PAYPAL_PAYMENT });

  return get(state, 'payment.payPalPaymentRequest', {});
};

export const setPayPalServiceUnavailable = () => ({
  type: SET_PAYPAL_SERVICE_UNAVAILABLE,
});

export const payPalPaymentOnApprove =
  (firstName, lastName, phone, billingAddress, nonce) => async (dispatch, getState) => {
    const payPalPaymentProcessing = get(getState(), 'payment.payPalPaymentProcessing');
    if (payPalPaymentProcessing) return;

    const billingAddressRequest = {
      addressee: {
        firstName,
        lastName,
      },
      address: {
        addressLine1: billingAddress.line1,
        addressLine2: billingAddress.line2,
        addressLine3: billingAddress.line3,
        addressLine4: billingAddress.line4,
        townOrCity: billingAddress.city,
        postcode: billingAddress.postalCode,
        countryCode: billingAddress.countryCode,
        countyStateOrProvince: billingAddress.state,
      },
      phoneNumber: phone,
    };

    dispatch({ type: PAYPAL_PAYMENT_STARTED });

    const validateAddressResult = validate({
      ...billingAddressRequest.address,
      ...billingAddressRequest.addressee,
      phoneNumber: billingAddressRequest.phoneNumber,
    }, {
      config: getPayPalBillingDetailsFormConfig({
        actions: {},
        countryCode: billingAddress.countryCode,
        configExtras: {
          titleFieldOptional: true,
        },
      }),
    });

    if (!isEmpty(validateAddressResult)) {
      dispatch({
        type: `${UPDATE_BILLING_ADDRESS}.FAILED`,
        error: {
          code: errorCodeConstants.INCOMPLETE_ADDRESS_DETAILS,
        },
        billingAddressFromPaypal: billingAddressRequest,
        paypalNonce: nonce,
      });

      dispatch({ type: PAYPAL_PAYMENT_ENDED });
      return;
    }

    const { type } = await dispatch(updateBillingAddress(billingAddressRequest, nonce));
    if (type === `${UPDATE_BILLING_ADDRESS}.FAILED`) {
      dispatch({ type: PAYPAL_PAYMENT_ENDED });
      return;
    }

    const authenticationResponse = await dispatch(authenticatePayPalPayment({ nonce }));
    if (authenticationResponse.type === `${AUTHENTICATE_PAYPAL_PAYMENT}.SUCCESS`) {
      dispatch(submitOrderAndRedirect());
    }

    if (authenticationResponse.type === `${AUTHENTICATE_PAYPAL_PAYMENT}.FAILED`) {
      await dispatch(initPaymentPage());
      dispatch({ type: PAYPAL_PAYMENT_FAILED });
    }
  };

export const resubmitBillingAddress = nonce => async (dispatch, getState) => {
  const billingAddress = getBillingAddress(getState());

  return dispatch(payPalPaymentOnApprove(
    billingAddress.addressee.firstName,
    billingAddress.addressee.lastName,
    billingAddress.phoneNumber,
    {
      line1: billingAddress.address.addressLine1,
      line2: billingAddress.address.addressLine2,
      // addressLine3 and addressLine4 can only come from the invalid address modal when customer clicks 'add lines'
      line3: billingAddress.address.addressLine3,
      line4: billingAddress.address.addressLine4,
      city: billingAddress.address.townOrCity,
      postalCode: billingAddress.address.postcode,
      countryCode: billingAddress.address.countryCode,
      state: billingAddress.address.countyStateOrProvince,
    },
    nonce,
  ));
};

export const openPayPalStubsModal = () => async (dispatch) => {
  await dispatch(getPayPalPaymentRequest());
  dispatch({ type: PAYPAL_STUBS_MODAL_OPEN });
};

export const closePayPalStubsModal = () => ({
  type: PAYPAL_STUBS_MODAL_CLOSE,
});

export const payPalPaymentOnCancel = () => async (dispatch, getState) => {
  const payPalStubsModalOpen = get(getState(), 'payment.payPalStubsModalOpen');

  if (payPalStubsModalOpen) {
    dispatch(closePayPalStubsModal());
  }

  await dispatch(initPaymentPage());
  dispatch({ type: PAYPAL_PAYMENT_CANCEL });
};

export const payPalPaymentOnError = () => async (dispatch, getState) => {
  const payPalStubsModalOpen = get(getState(), 'payment.payPalStubsModalOpen');

  if (payPalStubsModalOpen) {
    dispatch(closePayPalStubsModal());
  }

  await dispatch(initPaymentPage());
  dispatch({ type: PAYPAL_PAYMENT_FAILED });
};

export const mockPayPalIncompleteBillingAddress = () => (dispatch) => {
  dispatch(closePayPalStubsModal());

  dispatch(payPalPaymentOnApprove(
    'James',
    'Ellroy',
    '07777777777',
    {
      line1: '新登镇',
      line2: 'An unusually long line to trigger line length errors',
      postalCode: 'N75PQ',
      countryCode: 'GB',
      state: 'London',
    },
    'nonce',
  ));
};

export const mockPayPalApprove = (nonce = 'nonce') => async (dispatch) => {
  dispatch(closePayPalStubsModal());

  dispatch(payPalPaymentOnApprove(
    'James',
    'Ellroy',
    '07777777777',
    {
      line1: 'addressLine1',
      line2: 'addressLine2',
      city: 'townOrCity',
      postalCode: 'SW1E 5NN',
      countryCode: 'GB',
      state: 'countyStateOrProvince',
    },
    nonce,
  ));
};

export const mockPayPalApproveUS = (nonce = 'nonce') => async (dispatch) => {
  dispatch(closePayPalStubsModal());

  dispatch(payPalPaymentOnApprove(
    'James',
    'Ellroy',
    '+1650-555-5555',
    {
      line1: '1600 Amphitheatre Parkway',
      line2: 'Mountain View',
      city: 'CA',
      postalCode: '94 043',
      countryCode: 'US',
      state: 'NJ',
    },
    nonce,
  ));
};

export const closeTooltip = () => {
  window.dispatchEvent(
    new CustomEvent(TOOLTIP_OPENED_EVENT_NAME, { detail: { id: MARKETING_PREFERENCES_TOOLTIP_ID } }),
  );
};

export const loadPayPalScripts = () => async (dispatch, getState) => {
  const state = getState();

  if (env.isClientNonProd) {
    return;
  }

  if (!isPayPalAvailable(state)) {
    return;
  }

  if (!state.user.deliveryPageLoaded) {
    return;
  }

  if (state.payment.payPalScriptsLoaded) {
    return;
  }

  if (state.payment.payPalScriptsLoading) {
    return;
  }

  await dispatch({
    type: PAYPAL_SCRIPTS_LOADING,
  });

  const onErrorCallback = () => {
    dispatch({
      type: PAYPAL_SCRIPTS_FAILED,
    });
  };

  loadScript({
    url: 'https://js.braintreegateway.com/web/3.66.0/js/client.min.js',
    onErrorCallback,
    onSuccessCallback: loadScript({
      url: 'https://js.braintreegateway.com/web/3.66.0/js/paypal-checkout.min.js',
      onErrorCallback,
      onSuccessCallback: () => {
        dispatch({
          type: PAYPAL_SCRIPTS_LOADED,
        });
      },
      async: true,
    })(),
    async: true,
  })();
};
